import { memo, useCallback, useRef, useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { InlineFieldContainer } from 'common/components/controls/InlineField/InlineFieldContainer';
import TextArea from 'common/components/textArea';
import {
  StyledMenu,
  TextAreaRenderer,
  QueueTextAreaWrapper,
  MissingTemplateTooltipWrapper,
} from './TextAreaRender.styles';
import MissingTemplateTooltip from '../CellComponent/MissingTemplateTooltip';

type TextAreaRenderProps = {
  isAiEnabled: boolean;
  assistiveAI: boolean;
  required: boolean;
  disabled?: boolean;
  fieldName: string;
  handleSaveValue: (value: Record<string, string>) => void;
  text: string;
  notInTemplate: boolean;
  texts: string[];
  isNoEditPermission: boolean;
  showNoPermissionPopup: () => void;
};

export const TextAreaRender = memo(
  ({
    isAiEnabled,
    assistiveAI,
    required,
    disabled = false,
    fieldName,
    handleSaveValue,
    text,
    notInTemplate,
    texts,
    isNoEditPermission,
    showNoPermissionPopup,
  }: TextAreaRenderProps) => {
    const labelRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [currentValue, setCurrentValue] = useState(text != null ? String(text) : '');
    const isOpened = !!anchorEl;

    const handleOpen = useCallback(() => {
      setAnchorEl(labelRef.current);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      handleSaveValue({ [fieldName]: currentValue });
    }, [handleSaveValue, currentValue, fieldName]);

    const handleClickAway = useCallback(() => {
      handleClose();
    }, [handleClose]);

    const handleValueChange = (value) => {
      setCurrentValue(value);
    };

    const handleTextAreaClick = () => {
      if (isNoEditPermission) {
        showNoPermissionPopup();
      }
    };

    const isEmptyRequiredValue = required && !currentValue;

    return notInTemplate ? (
      <MissingTemplateTooltipWrapper>
        <MissingTemplateTooltip />
      </MissingTemplateTooltipWrapper>
    ) : (
      <TextAreaRenderer ref={labelRef} isEmptyRequiredValue={isEmptyRequiredValue}>
        <InlineFieldContainer
          // @ts-ignore
          disabled={disabled}
          fullwidth
          fieldName={fieldName}
          readOnly
          onClick={handleOpen}
          required={required}
          text={currentValue}
          disableTooltip
          tooltipPlacement="bottom"
          tooltipTextAlignment="start"
          texts={texts}
          isAiEnabled={isAiEnabled}
          assistiveAI={assistiveAI}
        />
        <StyledMenu
          open={isOpened}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <QueueTextAreaWrapper>
              <TextArea
                value={currentValue}
                setValue={handleValueChange}
                disabled={disabled}
                onClick={handleTextAreaClick}
              />
            </QueueTextAreaWrapper>
          </ClickAwayListener>
        </StyledMenu>
      </TextAreaRenderer>
    );
  },
);
